/**
 * vue-error-codes.js — make a production Vue error say where it came from.
 *
 * In a production build Vue does not hand `app.config.errorHandler` the words
 * "transition hook"; it hands it the URL
 * `https://vuejs.org/error-reference/#runtime-9`. Our handler printed that URL
 * verbatim, so the console read
 *
 *     Global error: TypeError: Illegal invocation
 *     Component info: https://vuejs.org/error-reference/#runtime-9
 *
 * — two lines that name neither the frame nor the component. `runtime-9` is a
 * real, stable index into Vue's `ErrorTypeStrings`, so we can simply translate
 * it back and print the error's own stack alongside. (`Illegal invocation` is a
 * native call made on the wrong receiver; without the stack there is no way to
 * tell which one.)
 *
 * The table is Vue 3's `ErrorTypeStrings` for the numeric ErrorCodes — stable
 * since 3.3. An index we do not know is reported as-is rather than guessed at.
 *
 * Pure, no imports — `node --test` runs it directly.
 */

export const VUE_ERROR_TYPES = {
  0: 'setup function',
  1: 'render function',
  2: 'watcher getter',
  3: 'watcher callback',
  4: 'watcher cleanup function',
  5: 'native event handler',
  6: 'component event handler',
  7: 'vnode hook',
  8: 'directive hook',
  9: 'transition hook',
  10: 'app errorHandler',
  11: 'app warnHandler',
  12: 'ref function',
  13: 'async component loader',
  14: 'scheduler flush',
  15: 'component update',
  16: 'app unmount cleanup function'
}

const RUNTIME_RE = /error-reference\/?#runtime-(\d+)/

/**
 * Turn Vue's `info` argument into something a human can act on.
 * A dev build already passes words, and they come back unchanged.
 *
 *   'https://vuejs.org/error-reference/#runtime-9'
 *     → 'transition hook (https://vuejs.org/error-reference/#runtime-9)'
 */
export function describeVueErrorInfo(info) {
  const text = String(info === null || info === undefined ? '' : info).trim()
  if (!text) return 'unknown source'
  const hit = RUNTIME_RE.exec(text)
  if (!hit) return text
  const label = VUE_ERROR_TYPES[Number(hit[1])]
  return label ? `${label} (${text})` : text
}

/** The component the error came from, by name, or null when Vue gave none. */
export function componentNameOf(instance) {
  if (!instance || typeof instance !== 'object') return null
  const opts = instance.$options || instance.type || {}
  return opts.name || opts.__name || (instance.type && instance.type.name) || null
}

/**
 * One console-ready record of a Vue error: the stack (not just the message),
 * the decoded source, and the component when Vue names one.
 */
export function formatVueError(err, instance, info) {
  const stack = err && typeof err === 'object' && err.stack ? String(err.stack) : null
  return {
    message: (err && err.message) || String(err),
    stack,
    source: describeVueErrorInfo(info),
    component: componentNameOf(instance)
  }
}
